/**
 * The ONE place a vehicle's canonical URL is derived.
 *
 *   /l/:slug/:id        (the route named "ListingPage")
 *
 * It is a pure function of persistent listing data — the listing's id and title.
 * It never reads component state, a previous page, session storage or a "selected
 * vehicle", which is what makes a vehicle link copy-pasteable into a fresh browser
 * and safe to open in many tabs at once.
 *
 * When a listing has no usable id this returns null. Callers must then render
 * NO link — never "#", and never a different page standing in for the vehicle.
 */

import { createSlug } from './urlHelpers';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The listing's id as a string, accepting the API shape ({ uuid }) or a plain string. */
export const listingIdOf = listing => {
  const raw = listing && listing.id;
  const id = raw && typeof raw === 'object' ? raw.uuid : raw;
  return typeof id === 'string' && UUID_RE.test(id) ? id : null;
};

/** @returns {string|null} the canonical detail path, or null when the listing has no id */
export const vehiclePath = listing => {
  const id = listingIdOf(listing);
  if (!id) return null;
  const title = listing.attributes && listing.attributes.title;
  const slug = createSlug(typeof title === 'string' ? title : '') || 'vehicle';
  return `/l/${slug}/${id}`;
};
